import React, { useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Send as SendIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAppStore } from '../state/store';

interface DataControlsProps {
  compact?: boolean;
  selectedTaskIds?: Set<string>;
  currentTab?: number;
  onSendToSalesforce?: (() => Promise<void>) | null;
  sendingToSalesforce?: boolean;
  showEventDropdown?: boolean;
  selectedEventId?: string;
  onAddTask?: () => void;
}

const DataControls: React.FC<DataControlsProps> = ({ 
  compact = false, 
  selectedTaskIds = new Set(), 
  currentTab = 0,
  onSendToSalesforce,
  sendingToSalesforce = false,
  showEventDropdown = false,
  selectedEventId = '',
  onAddTask
}) => {
  const { exportToCsv, isLoading, error, setError } = useAppStore();

  const handleExport = useCallback(() => {
    try {
      const csvData = exportToCsv();
      
      // Create blob with UTF-8 BOM for Excel compatibility (cross-platform)
      const BOM = '\uFEFF'; // UTF-8 Byte Order Mark
      const blob = new Blob([BOM + csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `cascade-meeting-data-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setError(null);
    } catch (error) {
      setError('Failed to export CSV. Please try again.');
    }
  }, [exportToCsv, setError]);

  const handleSendToSalesforce = useCallback(async () => {
    if (onSendToSalesforce) {
      try {
        await onSendToSalesforce();
      } catch (error) {
        // Error handling is done in TasksGrid
      }
    }
  }, [onSendToSalesforce]);

  // Button should be disabled if:
  // - Not on Tasks tab (currentTab !== 0)
  // - No tasks selected
  // - Handler not available
  // - Currently sending
  // - Event dropdown is shown but no event is selected
  const isSendDisabled = 
    currentTab !== 0 || 
    selectedTaskIds.size === 0 || 
    !onSendToSalesforce || 
    sendingToSalesforce ||
    (showEventDropdown && !selectedEventId);

  if (compact) {
    const selectedCount = selectedTaskIds.size;
    const buttonText = sendingToSalesforce
      ? 'Sending...'
      : selectedCount > 0 
      ? `Send to Salesforce (${selectedCount})` 
      : 'Send to Salesforce';

    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {onAddTask && (
          <Button
            variant="contained"
            size="small"
            color="success"
            startIcon={<AddIcon />}
            onClick={onAddTask}
          >
            Add Task
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={sendingToSalesforce ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSendToSalesforce}
          disabled={isSendDisabled}
          sx={{ 
            '&.Mui-disabled': {
              opacity: 0.5
            }
          }}
        >
          {buttonText}
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={isLoading}
        >
          Export CSV
        </Button>
      </Box>
    );
  }

  const selectedCount = selectedTaskIds.size;
  const buttonText = sendingToSalesforce
    ? 'Sending...'
    : selectedCount > 0 
    ? `Send to Salesforce (${selectedCount})` 
    : 'Send to Salesforce';

  return (
    <Box sx={{ mb: 3 }}>
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Data Management
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {onAddTask && (
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={onAddTask}
            >
              Add Task
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={sendingToSalesforce ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handleSendToSalesforce}
            disabled={isSendDisabled}
          >
            {buttonText}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default DataControls;
